import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { lignesDeDetail, type ContexteJournal } from '../lib/journal.js';
import { langueCourante, useLibellesModules } from '../lib/modules.js';
import type { JournalEntree } from '../types.js';
import styles from './DetailJournal.module.css';

/** « etatPrecedent » → « Etat precedent » : le repli d'un champ qui n'a pas encore de libellé. */
function humaniser(cle: string): string {
  const mots = cle.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return mots.charAt(0).toUpperCase() + mots.slice(1);
}

function useContexteJournal(): ContexteJournal {
  const { t, i18n } = useTranslation('console');
  const modules = useLibellesModules();
  const langue = langueCourante(i18n.language);
  return {
    champ: (cle) => t(`journal.champs.${cle}`, { defaultValue: humaniser(cle) }),
    module: (id) => modules.libelle(id),
    oui: t('journal.valeurs.oui'),
    non: t('journal.valeurs.non'),
    vide: t('journal.valeurs.vide'),
    valeur: (cle, valeur) => {
      if (cle === 'roleConsole' && valeur.startsWith('venio_')) {
        return t(`roles.${valeur.slice('venio_'.length)}`, { defaultValue: valeur });
      }
      if (cle === 'plateforme')
        return t(`appsMobiles.plateformes.${valeur}`, { defaultValue: valeur });
      return null;
    },
    date: (iso) =>
      new Date(iso).toLocaleString(langue === 'en' ? 'en-GB' : 'fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
  };
}

/**
 * Le détail d'une ligne du journal : « champ : valeur », ou « champ : avant →
 * après » quand la ligne dit une modification. Jamais le JSON brut.
 */
export function DetailJournal({ entree }: { entree: JournalEntree }): JSX.Element {
  const { t } = useTranslation('console');
  const ctx = useContexteJournal();
  const lignes = lignesDeDetail(entree, ctx);
  if (lignes.length === 0) return <>{t('journal.aucunDetail')}</>;
  return (
    <ul className={styles.liste}>
      {lignes.map((ligne, index) => (
        <li key={`${ligne.champ}-${index}`}>
          {ligne.champ ? <span className={styles.champ}>{ligne.champ} : </span> : null}
          {ligne.avant !== undefined ? (
            <>
              <span className={styles.avant}>{ligne.avant}</span>
              <span aria-label={t('journal.devient')}> → </span>
              <span className={styles.apres}>{ligne.apres}</span>
            </>
          ) : (
            ligne.valeur
          )}
        </li>
      ))}
    </ul>
  );
}
